import { Menu as AntMenu } from 'antd'
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import * as routes from '../../../constants/routes'
import { RootState } from '../../../store/store'
import { addCountlyEvent } from '../../../utils/stats'
import { ChatBubble, ArrowDown, BarChart, Briefcase, Letter, Globe, House, Newspaper } from '../../common/icons'
import { Picture, QuestionCircleFilled, TestingIcon, People, Star, Events, Results } from '../../common/icons'
import Item from './item/item'
import './menu.ant.scss'
import Group from '../../common/icons/group'
import Sections from '../../common/icons/sections'
import Questions from '../../common/icons/questions'
import { ContainerOutlined } from '@ant-design/icons'
import useInvisibleScroll from '../../survey/hooks/invisible-scoll'
import MessagesBadge from '../../messages/messages-badge'
import { selectMessagesUnreadCount } from '../../messages/select-messages-unread'

const TESTING_MENU_COLLAPSED_KEY = 'testingMenuCollapsed'
const TESTING_MENU_COLLAPSED_EVENT = 'testingMenuCollapsedChange'

const getInitialTestingMenuCollapsed = () => {
  if (typeof window === 'undefined') {
    return false
  }

  return window.localStorage.getItem(TESTING_MENU_COLLAPSED_KEY) === 'true'
}

const surveyAdminMenuRouteMap: Record<string, string> = {
  [routes.GROUP_EDIT.split('/:')[0]]: routes.SURVEYS_GROUP,
  [routes.SECTION_EDIT.split('/:')[0]]: routes.SURVEYS_SECTION,
  [routes.QUESTION_VIEW.split('/:')[0]]: routes.SURVEYS_QUESTIONS,
  [routes.SURVEY_EDIT.split('/:')[0]]: routes.SURVEYS_CONTROL,
  [routes.SURVEYS_VIEW.split('/:')[0]]: routes.SURVEYS_CONTROL,
  [routes.SURVEYS_RESULTS_DETAIL.split('/:')[0]]: routes.SURVEYS_RESULTS,
}

const getSelectedMenuPath = (pathname: string) => {
  const rootLocation = /^\/[^/]+/g.exec(pathname)

  if (!rootLocation) {
    return '/'
  }

  if (rootLocation[0] === '/forum') return routes.MESSAGES

  if (rootLocation[0] === routes.SURVEYS_ADMIN) {
    const pathParts = pathname.split('/')
    const surveyAdminPath = `/${pathParts[1]}${pathParts[2] ? `/${pathParts[2]}` : ''}`

    return surveyAdminMenuRouteMap[surveyAdminPath] || surveyAdminPath
  }

  return rootLocation[0]
}

const Menu: FC = () => {
  const location = useLocation()
  const user = useSelector((state: RootState) => state.account.user)
  const unreadCount = useSelector(selectMessagesUnreadCount)
  const userAdminOrExpert = user?.isAdmin || user?.competence?.isDivisionExpert
  const userIsORP = user?.competence?.isOrp
  const userCanUseMailing = userAdminOrExpert || user?.competence?.isDeputyExpert
  const [testingMenuCollapsed, setTestingMenuCollapsed] = useState(getInitialTestingMenuCollapsed)

  useInvisibleScroll()

  const path = useMemo(() => getSelectedMenuPath(location.pathname), [location.pathname])

  useEffect(() => {
    const handleTestingMenuCollapsedChange = (event: Event) => {
      setTestingMenuCollapsed((event as CustomEvent<boolean>).detail)
    }

    window.addEventListener(TESTING_MENU_COLLAPSED_EVENT, handleTestingMenuCollapsedChange)

    return () => {
      window.removeEventListener(TESTING_MENU_COLLAPSED_EVENT, handleTestingMenuCollapsedChange)
    }
  }, [])

  const toggleTestingMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    setTestingMenuCollapsed((prevState) => {
      const nextState = !prevState

      if (typeof window !== 'undefined') {
        window.localStorage.setItem(TESTING_MENU_COLLAPSED_KEY, String(nextState))
        window.dispatchEvent(new CustomEvent(TESTING_MENU_COLLAPSED_EVENT, { detail: nextState }))
      }

      return nextState
    })
  }, [])

  return (
    <AntMenu mode="inline" className={'custom-menu dy-scrollbar-off'} defaultSelectedKeys={['/']} selectedKeys={[path]}>
      <Item route={routes.MAIN} title={'Главная'} icon={<House />} key={1} />
      <Item
        route={routes.ABOUT}
        title={'WorldSkills'}
        icon={<Globe />}
        key={2}
        onClick={() => addCountlyEvent('Переход в раздел О WorldSkills', { Источник: 'Меню' })}
      />
      <Item
        route={routes.NEWS}
        title={'Новости'}
        icon={<Newspaper />}
        key={3}
        onClick={() => addCountlyEvent('Переход в раздел новостей', { Источник: 'Меню' })}
      />
      {user && (
        <Item
          route={routes.MESSAGES}
          title={
            <span className="messages-menu-label">
              Сообщения
              <MessagesBadge count={unreadCount} scope={`${user.id}:${user.competence?.id}`} />
            </span>
          }
          icon={<ChatBubble />}
          className="messages-menu-item"
          key={20}
        />
      )}
      <Item route={routes.CHAMPIONSHIPS} title={'Чемпионаты'} icon={<BarChart />} key={4} />
      {userAdminOrExpert ? <Item route={routes.RATING} title={'Оценки'} icon={<Star />} key={18} /> : null}
      {userAdminOrExpert || userIsORP ? (
        <Item route={routes.PARTICIPANTS} title={'Участники'} icon={<People />} key={5} />
      ) : null}
      <Item route={routes.COMPETENCIES} title={'Компетенции'} icon={<Briefcase />} key={6} />
      <Item route={routes.EVENTS} title={'Календарь событий'} icon={<Events />} key={7} />
      <Item route={routes.GALLERY} title={'Галерея'} icon={<Picture />} key={8} />
      {userAdminOrExpert ? (
        <>
          <AntMenu.Item key={99} className={'item-line-full'} />
          <Item
            route={routes.SURVEYS_CONTROL}
            title={'Тестирования'}
            icon={<TestingIcon />}
            className={'up-line testing-menu-control'}
            extra={
              <button
                type={'button'}
                data-burger-menu-keep-open={'true'}
                className={`testing-menu-control__toggle${testingMenuCollapsed ? ' collapsed' : ''}`}
                onMouseDown={(event) => {
                  event.preventDefault()
                  event.stopPropagation()
                }}
                onClick={toggleTestingMenu}
                aria-label={testingMenuCollapsed ? 'Развернуть блок тестирований' : 'Свернуть блок тестирований'}
              >
                <ArrowDown color="#025EA1" width={12} height={6} />
              </button>
            }
            key={12}
          />
          <Item
            route={routes.SURVEYS_GROUP}
            title={'Группы'}
            icon={<Group />}
            className={`testing-menu-child${testingMenuCollapsed ? ' collapsed' : ''}`}
            key={13}
          />
          <Item
            route={routes.SURVEYS_SECTION}
            title={'Секции'}
            icon={<Sections />}
            className={`testing-menu-child${testingMenuCollapsed ? ' collapsed' : ''}`}
            key={14}
          />
          <Item
            route={routes.SURVEYS_QUESTIONS}
            title={'Вопросы'}
            icon={<Questions />}
            className={`testing-menu-child${testingMenuCollapsed ? ' collapsed' : ''}`}
            key={15}
          />
          <Item
            route={routes.SURVEYS_RESULTS}
            title={'Результаты'}
            icon={<Results />}
            className={`testing-menu-child down-line${testingMenuCollapsed ? ' collapsed' : ''}`}
            key={16}
          />
          <AntMenu.Item key={98} className={'item-line-full'} />
        </>
      ) : (
        <>{!user || <Item route={routes.SURVEYS} title={'Тестирование'} icon={<TestingIcon />} key={11} />}</>
      )}
      {!userAdminOrExpert && userCanUseMailing ? <AntMenu.Item key={97} className={'item-line-full'} /> : null}
      {userCanUseMailing ? (
        <Item route={routes.MAILING} title={'Рассылка'} icon={<Letter />} className={'up-line'} key={19} />
      ) : null}
      <Item
        route={routes.VOTING}
        title={'Голосования'}
        icon={<ContainerOutlined style={{ fontSize: '24px', color: '#818C99' }} />}
        key={17}
      />
      <Item route={routes.FAQ} title={'FAQ'} icon={<QuestionCircleFilled />} key={9} />
      <Item
        route={routes.CONTACTS}
        title={'Написать нам'}
        icon={<Letter />}
        key={10}
        onClick={() => addCountlyEvent('Обратная связь', { Действие: 'Форма открыта', Источник: 'Меню' })}
      />
    </AntMenu>
  )
}

export default Menu
